// from assets import number, AssetType, Assets, FundExhausted
// from retirement_state import RetirementState

import {bisect_predicate_range} from './algo.ts';
import {Assets, AssetTypeId} from './assets.ts';
import {assert} from './assert.ts';
import type {RetirementState} from './retirement_state.ts';

export function sourceWithdrawals(
  rs: RetirementState,
  is_sufficient: (assets: Assets) => boolean,
  earlyPensionWithdrawalPerYear: number,
  noisy_logging: boolean,
): Assets {
  // Select how much to withdraw from each asset pool.

  function gross_amount_sufficient(amount: number): boolean {
    const withdrawals: Assets = _gimme(
      amount, rs, earlyPensionWithdrawalPerYear);

    const result: boolean = is_sufficient(withdrawals);
    return result;
  }

  const available_gross_assets: Assets = rs.assets.income_sources;

  if (!is_sufficient(available_gross_assets)) {
    return available_gross_assets;
  }

  const zeroAssets = new Assets();
  if (is_sufficient(zeroAssets)) {
    // It could be that we're asking for so little, that final salary pension
    // and state pension are adequate/superfluous.
    return zeroAssets;
  }

  const sufficient_gross_sum: number = bisect_predicate_range(
    gross_amount_sufficient,
    0,
    available_gross_assets.sum,
    0.01,
  );

  const sufficient_withdrawals = _gimme(
    sufficient_gross_sum, rs, earlyPensionWithdrawalPerYear);

  if (noisy_logging) {
    console.log(`sufficient_withdrawals: ${sufficient_withdrawals.toString()}`);
  }

  return sufficient_withdrawals;
}

type Tranche = {
  // Possible opportunity to take money out of asset pool.
  name: string;
  type: AssetTypeId;

  // withdrawal_amount is the amount that could be taken from the pool,
  // not the amount yielded after tax.
  amount: (
    rs: RetirementState,
    assets: Assets,
    withdrawal_amount: number
  ) => number;
};

function getTranches(earlyPensionWithdrawalPerYear: number): Tranche[] {
  return [
    // PENSION: MIN(50k, balance, required) if allowed else 0
    // UNPROTECTED: MIN(balance, required)
    // PENSION: MIN(balance, required) if allowed else 0
    // ISA: MIN(balance, required)
    {
      name: 'Early Pension',
      type: AssetTypeId.Pension,
      amount: (rs: RetirementState, available_assets: Assets, amt: number) =>
        rs.can_receive_private_pension
          ? Math.min(
            available_assets.balance(AssetTypeId.Pension),
            earlyPensionWithdrawalPerYear,
            amt,
          )
          : 0,
    },
    {
      name: 'Unprotected',
      type: AssetTypeId.Unprotected,
      amount: (
        _rs: RetirementState,
        available_assets: Assets,
        amt: number,
      ) =>
        Math.min(amt, available_assets.balance(AssetTypeId.Unprotected)),
    },
    {
      name: 'ISA',
      type: AssetTypeId.ISA,
      amount: (
        _rs: RetirementState,
        available_assets: Assets,
        amt: number,
      ) => Math.min(amt, available_assets.balance(AssetTypeId.ISA)),
    },
    {
      name: 'Pension',
      type: AssetTypeId.Pension,
      amount: (rs: RetirementState, available_assets: Assets, amt: number) =>
        rs.can_receive_private_pension
          ? Math.min(available_assets.balance(AssetTypeId.Pension), amt)
          : 0,
    },
  ];
}

// Generate Assets object representing feasible (not overdrawing) withdrawals
// whose components sum to $this_much.
// this_much is a gross figure that may not deliver as big a net income.
function _gimme(
  this_much: number,
  rs: RetirementState,
  earlyPensionWithdrawalPerYear: number,
): Assets {
  let available_assets: Assets = rs.assets.income_sources;
  let withdrawals: Assets = new Assets();
  const tranches = getTranches(earlyPensionWithdrawalPerYear);

  for (const tranche of tranches) {
    const still_needed: number = this_much - withdrawals.sum;
    if (Math.abs(still_needed) < 0.01) {
      return withdrawals;
    }

    assert(still_needed >= 0, 'overshot amount needed');
    const recommended_amount: number = tranche.amount(rs, available_assets, still_needed);
    available_assets = available_assets.debit(tranche.type, recommended_amount);
    withdrawals = withdrawals.credit(tranche.type, recommended_amount);
  }

  try {
    assert(withdrawals.sum <= this_much + 0.01, 'withdrawals insufficient');
  } catch (ae) {
    console.warn('Throwing from _gimme');
    throw (ae as Error);
  }

  return withdrawals;
}
